// 05m step C — the entries, reachable from a function that has no props.
//
// WHY THIS EXISTS
//   `pname` (utils.ts) renders `i.title` and `faddr` renders `i.country`.
//   Both are stored TAXONOMY values, both are rendered raw, and `pname` alone
//   has 194 call sites. Threading `person_title` entries through 194 call
//   sites is not a change anyone can review, and it is not what makes the
//   render correct — the entries are already in memory, in useTaxonomy's
//   module-level cache. They were simply not reachable from a plain function.
//
//   This is the same seam `pickEntryLabel` already uses for the same reason:
//   it reads `window.__lxLang` because a non-React call site cannot call a
//   hook (taxonomyMap.ts:420). One registry, one direction, no cycle —
//   useTaxonomy WRITES, render helpers READ, and this file imports nothing.
//
// WHY IT IS LOAD-BEARING FOR 05n, not a convenience
//   Today these columns hold LABELS, so rendering them raw is wrong but
//   legible: a French-saved client shows "Mme" to an English reader. After
//   05n converts them, the column holds the CODE — and rendering raw would
//   print `mr Jean Dupont` on 780 prod rows. **The migration cannot land
//   before the render path resolves.** That ordering is the whole reason step
//   C comes first.
//
// STALENESS IS BOUNDED AND VISIBLE. A read before the kind has loaded returns
// null, and every resolver falls back to the raw stored value — which is
// exactly today's behaviour, so the pre-hydration frame is unchanged rather
// than blank. useTaxonomy's subscribers re-render every consumer when the
// fetch lands, so the corrected label arrives with the next paint.

import type { TaxEntry } from "./taxonomyTypes";

const registry = new Map<string, TaxEntry[]>();

// Called by useTaxonomy on every cache write (fetch, fallback, invalidate).
export function publishTaxonomy(kind: string, entries: TaxEntry[] | null) {
  if (entries && Array.isArray(entries)) registry.set(kind, entries);
  else registry.delete(kind);
}

// null when this kind has not loaded — never an empty array, so a caller
// cannot mistake "not yet" for "the firm has no entries".
export function taxonomyEntriesOf(kind: string): TaxEntry[] | null {
  const e = registry.get(kind);
  return e && e.length ? e : null;
}

// Test seam only. Nothing in the app calls this.
export function __resetTaxonomyRegistry() {
  registry.clear();
}
